import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Collapse,
  Input,
  InputNumber,
  message,
  Modal,
  Radio,
  Select,
  Switch,
  Tag,
  Tooltip
} from 'antd';
import style from '../index.scss';
import QueryBuilder from '../QueryBuilder/QueryBuilder';
import {
  labelWithinBlock,
  renderOptionBody
} from '../QueryBuilder/FieldSelectWithSubtitle';
// Pure option shaping, extracted so it can be tested without this module's Redux/component tree.
import {
  formatFieldsForDropdown,
  formatFieldsWithAPIData,
  groupFieldOptions
} from './fieldOptions';
import { connect } from 'react-redux';
import { InfoCircleOutlined } from '@ant-design/icons';
import { orderBy, toLower } from '../../../../../util/lodashlike';
import { triggeredOnOptions } from '../config';
import {
  normalizeApplicableTo,
  groupByOptionsFor,
  feedingClassFor,
  fieldsFor
} from './applicableTo';
import {
  addFunction,
  patchFunctionArgs,
  replaceVariant
} from './functionInfoUpdates';
import { selectRuleById } from './selectRuleById';
import { renameFunctionReferences } from '../renameFunctionReferences';
import {
  cloneTreeWithFreshIds,
  mergeTreesAsAnd,
  isEmptyTree
} from '../QueryBuilder/treeUtils';
import {
  isIncludeCurrentEndConflict,
  INCLUDE_CURRENT_END_CONFLICT_HINT
} from '../includeCurrentTransactionValidation';
import {
  isMappingIncludeCurrentConflict,
  MAPPING_INCLUDE_CURRENT_HINT
} from '../groupingKeyMappingValidation';
// FIN-12351: Perspective / Swap decision logic. Pure, unit-tested — see groupingKeyPairs.js for
// the contract, the three validation gates, and why the mapping KEY must keep the stored spelling.
import {
  pairsFor,
  buildSpellingResolver,
  controlFor,
  presetFromMap,
  mapFromPerspective,
  defaultOptionOf,
  mapFromSwap,
  reconcileGroupingKeys,
  strandedGroupingKeys,
  isPairedKey,
  strandedKeysWarning,
  partyOf,
  readsAsSentence,
  perspectiveHint,
  perKeyControls,
  mapWithKeyValue
} from './groupingKeyPairs';

const { Panel } = Collapse;

const getToolTip = (title) => {
  return (
    <div style={{ marginLeft: '10px', marginTop: '-3px' }}>
      <Tooltip title={title}>
        <InfoCircleOutlined twoToneColor="#000" />
      </Tooltip>
    </div>
  );
};

// FIN-12586: one rendering for every field dropdown in this drawer. This was a local copy of
// the two-line treatment; the picker's version puts the raw column on hover and suppresses it
// when the label already states it, so the two dropdowns no longer disagree about what a field
// option looks like.
const renderFieldOption = (label, rawName, trail = null) =>
  renderOptionBody({
    label: trail ? labelWithinBlock(label, trail) : label,
    tooltip: rawName,
    prefix: ''
  });

const fieldOptionFilter = (input, option) => {
  const q = (input || '').toLowerCase();
  const label = (option?.label || '').toString().toLowerCase();
  const value = (option?.value || '').toString().toLowerCase();
  return label.includes(q) || value.includes(q);
};

/**
 * FIN-13506: antd hands `tagRender` the matching option's label, and falls back to the raw VALUE
 * when the selection matches no option. A Group By key outside the current picker list is exactly
 * that case — the allowlist narrows the options, or the key belongs to the other event class — so
 * the tag printed the storage column (`user_id`) where every other control shows "User ID".
 *
 * Resolve through the same full-catalogue resolver the mapping row and the "Reads as" line already
 * use (`resolverOptions` → `spellingResolver.labelOf`), which knows both event classes and both
 * spellings. Factory rather than a bare component so the resolver can be closed over.
 */
export const renderFieldTagWith = (labelOf) => (tagProps) => {
  // Read off a single param rather than destructuring in the signature: this arrow returns JSX, so
  // react/prop-types treats it as a component and would demand propTypes for each destructured name.
  const { label, value, closable, onClose } = tagProps;
  // A real option matched when antd's label differs from the value; otherwise it is the fallback
  // and we resolve the id ourselves.
  const display = label && label !== value ? label : labelOf(value);
  return (
    <Tooltip title={value}>
      <Tag closable={closable} onClose={onClose} style={{ marginRight: 3 }}>
        {display}
      </Tag>
    </Tooltip>
  );
};

const TIME_OPTIONS = [
  {
    value: 'day',
    label: 'Day/s'
  },
  {
    value: 'hour',
    label: 'Hour/s'
  },
  {
    value: 'minute',
    label: 'Minute/s'
  },
  {
    value: 'second',
    label: 'Second/s'
  },
  {
    value: 'month',
    label: 'Month/s'
  },
  {
    value: 'year',
    label: 'Year/s'
  }
];

const TIMESPAN_OPTIONS = [
  {
    label: 'Minutes of Day',
    value: 'mins_of_day'
  },
  {
    label: 'Hour',
    value: 'hour'
  }
];

const TIME_IN_SECONDS = {
  year: 365 * 30 * 24 * 60 * 60,
  month: 30 * 24 * 60 * 60,
  day: 24 * 60 * 60,
  hour: 60 * 60,
  minute: 60,
  second: 1
};

// Exported UNCONNECTED so a test can drive the REAL handlers. `connect` needs a store, and the
// behaviour that has twice gone wrong here — onChangeSelectType reconciling the mapping against a
// new Group By selection — has nothing to do with redux. The RTL suite renders the CARD
// (AggregateypeFunction) with `onChangeSelectType={jest.fn()}`, so nothing executed this component's
// own handler and a ReferenceError in it passed 4494 tests.
export const FunctionsUnconnected = ({
  typology,
  variantInfo = null,
  typoIndex,
  variantIndex,
  setVariantRenderData,
  blackList,
  isReadOnly,
  onDirtyField,
  disableActions,
  metaData,
  validationStatus = {}
}) => {
  // FIN-13542: `metaData` is null on any route that never dispatches
  // fetchQueryBuilderMetadata (e.g. the alert Explanation drawer,
  // see ExplanationPanel/index.js) — a bare `metaData || {}` still crashes
  // downstream, because `fields` etc. would then be `undefined` and this tab
  // dereferences them unconditionally (`aggregateFunction[fields[val]?.type]`,
  // `formatFieldsForDropdown(fields)`). Default every destructured member so a
  // missing/null metaData renders an empty-but-usable tab instead of throwing.
  const {
    fields = {},
    tooltips = {},
    aggregateFunction = {},
    matchMethodOptions = [],
    groupByOptions = [],
    // FIN-12351: {} until the `v6.4.0_grouping_key_pairs*.sql` seeds are applied, in which case no
    // Perspective / Swap control is offered anywhere and behaviour is exactly as before.
    groupingKeyPairs = {}
  } = metaData || {};
  const selectedRules = useMemo(
    () => selectRuleById(typology, variantInfo?.ruleId),
    [typology, variantInfo]
  );
  const [matchList, setMatchList] = useState([]);

  useEffect(() => {
    if (blackList && blackList.data && blackList.data.payload) {
      setMatchList(blackList.data.payload);
    }
  }, [blackList]);

  const [functionInfo, setFunctionInfo] = useState(variantInfo);
  const { inputType: triggeredOn = 'transaction' } = variantInfo || {};
  // FIN-13484: advisory findings for THIS variant from the last /validate run. Keyed the same
  // way the Typologies-tab badges are (scenario typology id + ruleId).
  const ruleWarnings = useMemo(() => {
    const tid = typology?.typologyId ?? typology?.id;
    return (
      validationStatus?.[tid]?.variants?.[variantInfo?.ruleId]?.warnings || []
    );
  }, [validationStatus, typology, variantInfo]);
  // FIN-12351: `onChangeSelectType` below reconciles the mapping against the new Group By
  // selection, and that reconciliation needs the canonical <-> legacy resolver. The card builds
  // its own from `groupByArr`, but that is CHILD state and is not in scope here — referencing it
  // from this component passed every unit test (they call reconcileGroupingKeys directly) while
  // reaching pruneMapping as `undefined`, where the `resolver ? ... : identity` guard silently
  // degraded it to the exact-string match it was written to replace: a legacy-keyed mapping was
  // dropped the moment any Group By edit canonicalised the keys. Caught by no-undef, not by a test.
  //
  // Built from the full `groupByOptions[triggeredOn]` rather than the card's allowlist-filtered
  // `groupByArr`. A resolver only needs the spelling pairs, and the superset resolves strictly
  // more ids — a key filtered out of one function's picker still has a spelling to reconcile.
  // The UNION of both event classes' Group By options, not just `triggeredOn`'s.
  //
  // FIN-13484: grouping keys belong to the classes a function AGGREGATES (`applicableTo`), which
  // can differ from `triggeredOn` (a transaction variant grouping activity history by `user_id`),
  // so the resolver must know both classes' spellings. Ids are globally unique and this is
  // display-only, so resolving more is free.
  const resolverOptions = useMemo(
    () => [
      ...(groupByOptions?.transaction || []),
      ...(groupByOptions?.activity || [])
    ],
    [groupByOptions]
  );
  const spellingResolver = useMemo(
    () => buildSpellingResolver(resolverOptions),
    [resolverOptions]
  );
  const [functionNames, setFunctionNames] = useState([]);

  useEffect(() => {
    const names = functionInfo?.function?.map((item) => item.name) || [];
    setFunctionNames(names);
  }, [functionInfo]);

  const onChangeFunction = (e, funIndex, key) => {
    setFunctionInfo((prev) => {
      const updated = { ...prev };
      updated.function = [...prev.function];

      updated.function[funIndex] = {
        ...prev.function[funIndex],
        [key]: e.target.value
      };

      if (key === 'functionName') {
        if (e.target.value === 'aggregation') {
          updated.function[funIndex].args = {
            lookback: { start: 0, end: 0 },
            lookbackType: 'calendar',
            applicableTo: prev.function[funIndex].args?.applicableTo || [
              triggeredOn
            ]
          };
        } else if (e.target.value === 'screening') {
          updated.function[funIndex].args = {
            applicableTo: prev.function[funIndex].args?.applicableTo || [
              triggeredOn
            ]
          };
        }
      }
      return updated;
    });
    onDirtyField();
  };

  const onChangeFunctionName = (e, funcIndex) => {
    const value = e.target.value;
    const duplicateName = functionNames.find(
      (item, index) => index !== funcIndex && toLower(item) === toLower(value)
    );

    if (duplicateName) {
      message.error(
        `Function name "${value}" is already exists. Please provide unique name`,
        5
      );
      disableActions(true);
    } else {
      disableActions(false);
    }

    setFunctionNames((prev) => {
      const updated = [...prev];
      updated[funcIndex] = value;
      return updated;
    });
  };

  const onBlurFunctionName = (e, funcIndex) => {
    const value = e.target.value;

    if (!value) {
      disableActions(true);
      message.error('Function name is required', 5);
      return;
    }

    if (toLower(value) !== toLower(functionInfo.function[funcIndex].name)) {
      const duplicateName = functionNames.find(
        (item, index) => index !== funcIndex && toLower(item) === toLower(value)
      );

      if (!duplicateName) {
        setFunctionInfo((prev) => {
          const updated = [...prev.function];
          const existingName = updated[funcIndex].name;

          // FIN-13190 — rewrite every reference to the old name, at any depth, on either
          // side of the comparison, and inside function arguments. The previous version
          // walked only the top level of children1 and only matched a bare string
          // left-hand side, so a renamed function referenced from a sub-group, from the
          // right-hand side (`f2 > f1`), or from inside another function's arguments was
          // left pointing at a name that no longer existed.
          const formulas = renameFunctionReferences(
            prev?.functionAggregation,
            existingName,
            value
          );

          updated[funcIndex] = { ...updated[funcIndex], name: value };

          return {
            ...prev,
            function: updated,
            functionAggregation: formulas
          };
        });
      }
    }
    onDirtyField();
  };

  const onChangeCalendarType = (e, funIndex, key) => {
    setFunctionInfo((prev) =>
      patchFunctionArgs(prev, funIndex, { [key]: e.target.value })
    );
    onDirtyField();
  };

  // FIN-12351: single writer for `args.groupingKeyToValueMapping`. An EMPTY map is DELETED rather
  // than stored: `{}` decodes fine on the BE, but omitting the key keeps a rule that never used a
  // perspective byte-identical to before (JSON.stringify drops undefined). An identity ENTRY
  // ({X: X}) is never written either — see mapFromPerspective.
  //
  // Returns a NEW args object rather than mutating, so it composes with `patchFunctionArgs`
  // (640_fix) — which rebuilds `functionInfo` immutably and would not see an in-place edit.
  const withMapping = (args, mapping) => {
    const next = { ...args };
    if (!mapping || Object.keys(mapping).length === 0) {
      delete next.groupingKeyToValueMapping;
    } else {
      next.groupingKeyToValueMapping = mapping;
    }
    return next;
  };

  // Write a mapping produced by the Perspective radio or the Swap toggle.
  const onChangeGroupingKeyMapping = (mapping, funIndex) => {
    setFunctionInfo((prev) =>
      patchFunctionArgs(
        prev,
        funIndex,
        withMapping(prev.function[funIndex].args, mapping),
        true
      )
    );
    onDirtyField();
  };

  const onChangeSelectType = (val, funIndex, key) => {
    setFunctionInfo((prev) => {
      const patched = patchFunctionArgs(prev, funIndex, {
        [key]: val,
        ...(key === 'field' ? { aggregationFunction: '' } : {})
      });
      if (key !== 'groupingKeys') return patched;
      // FIN-12351: a groupingKeyToValueMapping entry whose key is no longer a grouping key fails
      // BE validation on save ("… are not grouping keys of indicator") — from a Group By edit that
      // looks unrelated to the mapping. Reconcile in the same update.
      //
      // This needs `replace`, not a patch: pruning can end with NO mapping at all, and the field
      // must then be ABSENT rather than `{}` (§6.5 — an untouched legacy rule stays byte-identical
      // in the saved payload). A merge-patch cannot express a deletion.
      const nextArgs = patched.function?.[funIndex]?.args;
      if (!nextArgs) return patched;
      // reconcileGroupingKeys re-keys survivors to the spelling Group By now holds, rather than
      // dropping a legacy-keyed mapping the moment an edit canonicalises the keys.
      return patchFunctionArgs(
        patched,
        funIndex,
        reconcileGroupingKeys(nextArgs, val, spellingResolver),
        true
      );
    });
    // FIN-7250: changing Aggregate Field clears the Aggregate Function (valid
    // combinations differ). A blank aggregationFunction fails BE decode on save
    // with an unhelpful cursor-path error, so block save and name the missing
    // option here instead. Re-enable once it is re-selected.
    if (key === 'field') {
      disableActions(true);
      message.error(
        'Aggregate Function is required — please re-select it after changing Aggregate Field',
        5
      );
    }
    if (key === 'aggregationFunction') {
      disableActions(!val);
    }
    onDirtyField();
  };

  const onChangeMatchSelectType = (val, funIndex, key) => {
    setFunctionInfo((prev) =>
      patchFunctionArgs(prev, funIndex, { [key]: val })
    );
    onDirtyField();
  };

  const onChangeCalendarLookback = (val, funIndex, key) => {
    setFunctionInfo((prev) =>
      patchFunctionArgs(prev, funIndex, { lookback: val })
    );
  };

  useEffect(() => {
    if (setVariantRenderData) {
      // FIN-13389: immutable write-back. This used to mutate `prev` in place before
      // returning a new outer array, leaving every nested level referentially unchanged
      // so consumers memoising on them stayed stale.
      setVariantRenderData((prev) =>
        replaceVariant(prev, typoIndex, variantIndex, functionInfo)
      );
    }
  }, [functionInfo]);

  const addNewFunction = () => {
    // FIN-13389: `function.push(...)` then a shallow copy of the wrapper left the ARRAY
    // identity unchanged, so QueryBuilder's `allFunc` useMemo — keyed on
    // `variantInfo?.function` — never recomputed and the new function was never selectable
    // as a condition on the Formula tab. The Functions tab looked fine because it reads the
    // array directly during render.
    setFunctionInfo((prev) =>
      addFunction(prev, {
        args: {
          applicableTo: [triggeredOn]
        },
        functionName: 'screening',
        name: 'function1' + (prev.function?.length || 0)
      })
    );
    onDirtyField();
  };

  // FIN-13484: single class only. The two both-class entries ('activity,transaction' and
  // 'transaction,activity') decoded to the SAME Set on the BE, so they described an identical
  // rule and differed only in which class's order groupByOptionsFor used when intersecting the
  // catalogues — a dropdown ordering detail exposed as a user-facing choice. Worse, under the
  // native-only policy a grouping key must be a native column of EVERY class a function
  // aggregates, and the field catalogue keeps party identity strictly per class (transaction has
  // sender/receiver hashcode, activity has user id). So a both-class function could only ever
  // group by shared context — device, IP, session, loan — never by the customer.
  //
  // keycloak-qa bore that out: of 2267 stored rule rows exactly ONE used both-class, and both of
  // its functions were party-keyed (`user_id` over [activity,transaction] and `sender_hashcode`
  // over [transaction,activity]) — already inert, and authored with one of each order-variant,
  // which is the confusion this removes. "Count over this customer's transactions AND activities"
  // is now two single-class functions summed at the rule level via number.SUM over func operands.
  const applicableToOptions = useMemo(() => triggeredOnOptions, []);

  const getApplicableFields = useCallback(
    (applicableTo) => {
      // FIN-12176: applicableTo may arrive as a stringified array
      // ('["transaction"]') for uploaded/cloned typologies; normalize first.
      // FIN-13484: INTERSECTION of the selected classes' catalogues — the BE
      // rejects a field missing on any one of them, so the union offered
      // picks the save could never accept.
      return fieldsFor(
        fields,
        normalizeApplicableTo(applicableTo, [triggeredOn])
      );
    },
    [
      fields,
      triggeredOn,
      // FIN-11820: optional-chain through .args — the array element may be
      // undefined (function array shorter than variantIndex, freshly-cloned
      // variant with no .args yet, in-flight reducer update) and unguarded
      // .args throws during render, unmounting the drawer.
      variantInfo?.function?.[variantIndex]?.args?.applicableTo
    ]
  );

  // FIN-13538: `fields` and `groupByOptions` were built INLINE in the map below, so every render
  // handed the function card brand-new object identities. That is not cosmetic. The card's
  // `setGroupByArr` effect is keyed on `[fields, groupByOptions]`, and QueryBuilder's `configValue`
  // memo is keyed on `fields` with an effect that does `setConfig(configValue)`
  // (QueryBuilder.js:193). A fresh identity every render made both effects fire every render, each
  // setting state and provoking the next render, until React aborted at the update-depth limit
  // (minified error #185, swallowed by the tab ErrorBoundary).
  //
  // The visible symptom was that clicking a Perspective radio did nothing: the mapping write was
  // correct, but the render that would have shown it never completed, so the controlled
  // Radio.Group never received a new `value`. Memoise per function index so an identity changes
  // only when the underlying rule actually changes.
  //
  // `groupByOptions` became unstable with FIN-13484, which replaced the stable
  // `groupByOptions?.[triggeredOn]` lookup with a freshly-built intersection.
  const perFunctionFieldProps = useMemo(
    () =>
      (functionInfo?.function || []).map((item) => {
        const itemApplicableTo = item?.args?.applicableTo;
        return {
          fields: getApplicableFields(
            itemApplicableTo || [triggeredOn] || ['transaction']
          ),
          groupByOptions: groupByOptionsFor(
            groupByOptions,
            normalizeApplicableTo(itemApplicableTo, [triggeredOn])
          )
        };
      }),
    [functionInfo?.function, getApplicableFields, groupByOptions, triggeredOn]
  );

  // The reset CARRIES `groupingKeysList` over. It is the BE-seeded allowlist of Group By keys the
  // typology permits for this function — a property of the function template, not of the edit in
  // progress. Dropping it did not merely blank a field: `filterFieldOptions` reads an absent
  // allowlist as UNRESTRICTED, so a save after an Applicable To change silently removed the
  // function's Group By restriction and offered the whole catalogue on the next open. It also put
  // the picker (which filters on the template allowlist) and `groupByEmptyReason` (which reads the
  // live one) on different values, so the list could come back empty with nothing to explain it.
  //
  // The rebuild is immutable too. `[...prev.function]` copies the array but not its elements, so
  // the old `updated.function[funIndex].args = …` wrote through to the object in the previous
  // state.
  const onChangeApplicableTo = (value, funIndex) => {
    setFunctionInfo((prev) => {
      const fn = prev.function?.[funIndex];
      if (!fn) return prev;

      const applicableTo = value.split(',');
      let args = fn.args;
      if (fn.functionName === 'aggregation') {
        args = {
          lookback: { start: 0, end: 0 },
          lookbackType: 'calendar',
          applicableTo,
          groupingKeysList: fn.args?.groupingKeysList
        };
      } else if (fn.functionName === 'screening') {
        args = { applicableTo };
      }

      const nextFunctions = [...prev.function];
      nextFunctions[funIndex] = { ...fn, args };
      return { ...prev, function: nextFunctions };
    });
    onDirtyField();
  };

  return (
    <div>
      <Collapse
        accordion
        expandIconPosition={'right'}
        className={style.typologiesCollapse}
      >
        {functionInfo &&
          functionInfo.function &&
          functionInfo.function?.map((item, index) => {
            // FIN-11824: single defensive args read — every downstream
            // item.args.* access in this Panel needs to survive a freshly-
            // cloned variant or in-flight reducer state where args isn't
            // populated yet. Without this guard, accesses like
            // `item.args.applicableTo` would throw and unmount the
            // drawer (FIN-11820 family). Aggregation/screening function
            // shapes differ; the {} fallback yields undefined leaves
            // which downstream `|| default` clauses already absorb.
            const args = item.args || {};
            return (
              <Panel header={<span>{item.name}</span>} key={index}>
                <div className={style.functionbox}>
                  <div>
                    <label style={{ display: 'flex' }}>
                      Function Name
                      <span style={{ color: 'red' }}>*</span>
                      {getToolTip(tooltips?.function_name)}
                    </label>
                    <Input
                      disabled={isReadOnly}
                      value={functionNames[index]}
                      onChange={(e) => onChangeFunctionName(e, index)}
                      onBlur={(e) => onBlurFunctionName(e, index)}
                    />
                  </div>
                  <div className={style.functionTypeContainer}>
                    <div>
                      <label style={{ display: 'flex' }}>
                        Function Type<span style={{ color: 'red' }}>*</span>
                        {getToolTip(tooltips?.function_type)}
                      </label>
                      <SegmentButton
                        isReadOnly={isReadOnly}
                        value={item.functionName}
                        index={index}
                        onChangeFunction={onChangeFunction}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'flex' }}>
                        Applicable To<span style={{ color: 'red' }}>*</span>
                        {getToolTip(tooltips?.applicable_to)}
                      </label>
                      <Select
                        className={style.applicableTo}
                        disabled={
                          isReadOnly ||
                          !variantInfo?.variantName?.startsWith(
                            '##_TT_MM_QA_##'
                          )
                        }
                        placeholder="Please Select"
                        options={applicableToOptions}
                        value={
                          // FIN-13259 — `.toString()` on a STRINGIFIED array (some uploaded /
                          // cloned typologies persist '["transaction"]', see FIN-12176) yields
                          // '["transaction"]', which matches no applicableToOptions entry, so the
                          // Select rendered the raw string instead of the "Transaction" label.
                          // normalizeApplicableTo handles every persisted shape.
                          //
                          // The `|| triggeredOn || 'transaction'` tail MUST stay: for a genuinely
                          // empty array normalizeApplicableTo short-circuits on Array.isArray and
                          // returns [] rather than the fallback, so .join(',') is '' — falsy — and
                          // the tail is what supplies a value.
                          normalizeApplicableTo(args.applicableTo, [
                            triggeredOn
                          ]).join(',') ||
                          triggeredOn ||
                          'transaction'
                        }
                        onChange={(value) => onChangeApplicableTo(value, index)}
                      />
                    </div>
                  </div>
                </div>
                {item.functionName === 'aggregation' ? (
                  <AggregateypeFunction
                    item={item}
                    variantInfo={variantInfo}
                    typoIndex={typoIndex}
                    isReadOnly={isReadOnly}
                    variantIndex={variantIndex}
                    functionIndex={index}
                    onChangeSelectType={onChangeSelectType}
                    onChangeCalendarType={onChangeCalendarType}
                    onChangeCalendarLookback={onChangeCalendarLookback}
                    setVariantRenderData={setVariantRenderData}
                    onDirtyField={onDirtyField}
                    selectedRules={selectedRules}
                    fields={perFunctionFieldProps[index]?.fields}
                    tooltips={tooltips}
                    aggregateFunction={aggregateFunction}
                    // FIN-13484: keys are native to the classes being AGGREGATED (applicableTo),
                    // not to the triggering class — a transaction variant aggregating activity
                    // history groups by activity `user_id` and redirects the read value via the
                    // mapping. Intersection when applicableTo names both classes.
                    groupByOptions={perFunctionFieldProps[index]?.groupByOptions}
                    disableActions={disableActions}
                    // FIN-12351 / FIN-13484: the pair row is keyed [rule event type][feeding
                    // class], and the mapping it configures is
                    //     Map[<feeding class>.<groupingKey>, <triggeringEvent>.<valueField>]
                    //
                    // The first axis is `triggeredOn` (the value is read off the triggering
                    // event). The second is the class the function AGGREGATES — own-type for the
                    // ordinary case, the other class for a cross-type function (a transaction
                    // variant grouping activity history by `user_id`, cell transaction/activity).
                    // It is derived from `applicableTo`, never from `args.inputType`: that field
                    // defaults to transaction, is never written by this drawer, and does not reach
                    // shard identity (FraudRule.distinctGroupingKeys uses the RULE's inputType).
                    groupingKeyPairs={pairsFor(
                      groupingKeyPairs,
                      triggeredOn,
                      feedingClassFor(
                        normalizeApplicableTo(args.applicableTo, [triggeredOn]),
                        triggeredOn
                      )
                    )}
                    // Whether a grouping_key_pairs row reached the client AT ALL — lets the card
                    // tell "seed missing / deployment defect" from "no branch seeded for this
                    // event type", which is documented scope rather than a fault.
                    pairConfigPresent={
                      Object.keys(groupingKeyPairs || {}).length > 0
                    }
                    resolverOptions={resolverOptions}
                    ruleWarnings={ruleWarnings}
                    onChangeGroupingKeyMapping={onChangeGroupingKeyMapping}
                  />
                ) : (
                  <MatchTypeFunction
                    matchList={matchList}
                    onChangeMatchSelectType={onChangeMatchSelectType}
                    item={args}
                    index={index}
                    isReadOnly={isReadOnly}
                    selectedRules={selectedRules}
                    fields={perFunctionFieldProps[index]?.fields}
                    matchMethodOptions={matchMethodOptions}
                    tooltips={tooltips}
                  />
                )}
              </Panel>
            );
          })}
      </Collapse>
      {!isReadOnly &&
        variantInfo?.variantName?.startsWith('##_TT_MM_QA_##') && (
          <button className={style.addBtn} onClick={addNewFunction}>
            + Add new Function
          </button>
        )}
    </div>
  );
};

const MatchTypeFunction = ({
  item,
  index,
  onChangeMatchSelectType,
  matchList,
  isReadOnly,
  selectedRules,
  fields,
  matchMethodOptions,
  tooltips
}) => {
  const [matchField, setMatchField] = useState([]);
  const [matchListArr, setMatchListArr] = useState([...matchList]);
  const [matchMethodArr, setMatchMethodArr] = useState([]);

  useEffect(() => {
    // Match Field
    const defaultOptions = formatFieldsForDropdown(fields);
    const matchFieldData = formatFieldsWithAPIData(
      selectedRules.function[index]?.args?.screeningFieldList,
      defaultOptions,
      true
    );
    setMatchField(matchFieldData);

    // Match Method
    const matchMethodData = formatFieldsWithAPIData(
      selectedRules.function[index]?.args?.screeningMethodList,
      matchMethodOptions
    );
    setMatchMethodArr(matchMethodData);
  }, [fields]);

  return (
    <div className={style.matchtype}>
      <div className={style.matchmethod}>
        <div className="matchfield">
          <label style={{ display: 'flex' }}>
            Match Field<span style={{ color: 'red' }}>*</span>
            {getToolTip(tooltips?.match_field)}
          </label>
          <Select
            disabled={isReadOnly}
            value={item.screeningField}
            placeholder="Please Select"
            options={matchField}
            onChange={(e) =>
              onChangeMatchSelectType(e, index, 'screeningField')
            }
          />
        </div>
        <div className="matchlist">
          <label style={{ display: 'flex' }}>
            Match List<span style={{ color: 'red' }}>*</span>
            {getToolTip(tooltips?.match_list)}
          </label>
          <Select
            mode="multiple"
            disabled={isReadOnly}
            value={item.screeningList}
            placeholder="Please Select"
            onChange={(e) => onChangeMatchSelectType(e, index, 'screeningList')}
          >
            {matchListArr
              .sort((a, b) => a?.name?.localeCompare(b?.name))
              ?.map((f) => (
                <Select.Option key={f.name} value={f.name}>
                  {f.name}
                </Select.Option>
              ))}
          </Select>
        </div>
      </div>
      <div className={style.matchmethod}>
        <div className="matchlist">
          <label style={{ display: 'flex' }}>
            Method<span style={{ color: 'red' }}>*</span>
            {getToolTip(tooltips?.match_method)}
          </label>
          <Select
            disabled={isReadOnly}
            value={item.screeningMethod}
            placeholder="Please Select"
            options={matchMethodArr}
            onChange={(e) =>
              onChangeMatchSelectType(e, index, 'screeningMethod')
            }
          />
        </div>
        <div className="matchlist">
          <label style={{ display: 'flex' }}>
            Fuzzy Factor<span style={{ color: 'red' }}>*</span>
            {getToolTip(tooltips?.match_fuzzy_factor)}
          </label>
          <InputNumber
            disabled={isReadOnly}
            value={item.fuzzinessFactor}
            min={0}
            max={1}
            onChange={(val) =>
              onChangeMatchSelectType(val, index, 'fuzzinessFactor')
            }
          />
        </div>
      </div>
    </div>
  );
};

const CopyFromGlobalLink = ({
  variantInfo,
  typoIndex,
  variantIndex,
  functionIndex,
  currentTarget,
  setVariantRenderData,
  onDirtyField
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const sourceEmpty = isEmptyTree(variantInfo?.condition);
  const targetHasContent = !isEmptyTree(currentTarget);

  const apply = (mode) => {
    setVariantRenderData((prev) => {
      const next = prev;
      const fn = next[typoIndex].variants[variantIndex].function[functionIndex];
      const source = next[typoIndex].variants[variantIndex].condition;
      let resultTree;
      if (mode === 'merge' && !isEmptyTree(currentTarget)) {
        resultTree = mergeTreesAsAnd(currentTarget, source);
      } else {
        resultTree = cloneTreeWithFreshIds(source);
      }
      fn.args.aggregationCondition = resultTree;
      return [...next];
    });
    onDirtyField();
    setModalOpen(false);
  };

  const handleClick = () => {
    if (targetHasContent) {
      setModalOpen(true);
      return;
    }
    apply('replace');
  };

  if (sourceEmpty) {
    return (
      <Tooltip title="Set Global Conditions first">
        <span>
          <a style={{ color: 'rgba(0,0,0,0.25)', cursor: 'not-allowed' }}>
            Copy from Global Conditions
          </a>
        </span>
      </Tooltip>
    );
  }

  return (
    <>
      <a onClick={handleClick} style={{ fontSize: 12 }}>
        Copy from Global Conditions
      </a>
      <Modal
        visible={modalOpen}
        title="Copy Global Conditions"
        onCancel={() => setModalOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setModalOpen(false)}>
            Cancel
          </Button>,
          <Button key="merge" onClick={() => apply('merge')}>
            Merge (AND)
          </Button>,
          <Button key="replace" type="primary" onClick={() => apply('replace')}>
            Replace
          </Button>
        ]}
      >
        <p>
          This Aggregation Filter already has conditions. Choose{' '}
          <strong>Replace</strong> to overwrite, or <strong>Merge (AND)</strong>{' '}
          to wrap the existing filter and the Global Conditions under a new AND
          group.
        </p>
      </Modal>
    </>
  );
};

// Exported for FIN-12351's Perspective / Swap tests: the connected default export drags in the
// store and the whole drawer tree, while this sub-component is the entire surface under test.
export const AggregateypeFunction = ({
  item,
  typoIndex,
  variantIndex,
  functionIndex,
  variantInfo,
  onChangeCalendarType,
  onChangeSelectType,
  onChangeCalendarLookback,
  setVariantRenderData,
  isReadOnly,
  onDirtyField,
  selectedRules,
  fields,
  tooltips,
  aggregateFunction,
  groupByOptions,
  disableActions,
  groupingKeyPairs = {},
  pairConfigPresent = false,
  resolverOptions = null,
  ruleWarnings = [],
  onChangeGroupingKeyMapping
}) => {
  // FIN-11824: single defensive args read so every downstream args.*
  // access in this component (lookback, field, aggregationFunction,
  // groupingKeys, lookbackType, includeCurrentTransaction,
  // aggregationCondition, aggregationFunctionArgs, …) survives an
  // unpopulated/in-flight item.args. Without it any of the ~12 reads
  // below would throw at render time and unmount the drawer.
  const args = item.args || {};

  // The variant's own event type. `applicableTo` falls back to this everywhere else in the file
  // (see the Applicable To consumers below), and the "Reads as" sentence needs the same fallback
  // so an activity-triggered variant is never described as aggregating transactions.
  const { inputType: triggeredOn = 'transaction' } = variantInfo || {};

  // FIN-13532: a CROSS-TYPE function aggregates a class the rule is not triggered on, so the focal
  // event can never be a member of the aggregate — FraudRealtimeAggregationFunction filters state
  // to `applicableTo` BEFORE `includeCurrentTransaction` is applied, and the flag then drops only
  // the focal id, which that filter has already removed. Setting it changes no count. Product
  // decision on FIN-13532: show the control (so its absence is not mistaken for a missing feature)
  // but disable it and say why on hover, rather than letting it be checked and refused at save.
  const isCrossTypeFunction = !normalizeApplicableTo(args.applicableTo, [
    triggeredOn
  ]).includes(triggeredOn);
  const counted = triggeredOn === 'transaction' ? 'activity' : 'transaction';
  // FIN-13548: disabling it outright also trapped the rules that ALREADY have it checked — and
  // those are exactly the ones that need to turn it off. `includeCurrentTransaction` combined with
  // a `groupingKeyToValueMapping` is a BLOCKING error (validate G2), and the error tells the
  // analyst to uncheck a box the UI would not let them touch. keycloak-qa had 39 such variants
  // across typologies 383 and 392, none of them repairable in the drawer.
  //
  // So keep the guard one-way: never allow CHECKING it on a cross-type function, but always allow
  // UNCHECKING. Turning it off is safe by the same argument that makes turning it on pointless —
  // the focal event is not of the counted class, so the flag is inert either way and no count
  // moves. That leaves the only permitted action the one the error asks for.
  const includeCurrentLocked =
    isCrossTypeFunction && !args.includeCurrentTransaction;

  // FIN-13223 — dropdown options are keyed by the canonical field id since FIN-12874, but a rule
  // saved before that stores the legacy spelling. Without this the antd Select finds no matching
  // option and renders the raw id instead of the field's label. Display only: the stored value
  // stays legacy until the user re-picks, which then writes canonical.
  const toCanonicalId = (id) => fields?.[id]?.canonicalName || id;

  const [aggregateField, setAggregateField] = useState([]);
  const [groupByArr, setGroupByArr] = useState([]);
  const [aggregateFunctionList, setAggregateFunctionList] = useState([]);
  const [densityArgs, setDensityArgs] = useState(
    args.aggregationFunctionArgs || { fraudFunction: 'density_time' }
  );

  const getLookBackValue = useCallback((loopback = 0) => {
    let valueToSave;
    if (loopback === 0) {
      valueToSave = {
        value: 0,
        type: 'second'
      };
    } else {
      const SECONDS_PER_MINUTE = 60;
      const SECONDS_PER_HOUR = 60 * SECONDS_PER_MINUTE;
      const SECONDS_PER_DAY = 24 * SECONDS_PER_HOUR;
      const SECONDS_PER_MONTH = 30 * SECONDS_PER_DAY;
      const SECONDS_PER_YEAR = 365 * SECONDS_PER_MONTH;

      if (loopback % SECONDS_PER_YEAR === 0) {
        valueToSave = {
          value: loopback / SECONDS_PER_YEAR,
          type: 'year'
        };
      } else if (loopback % SECONDS_PER_MONTH === 0) {
        valueToSave = {
          value: loopback / SECONDS_PER_MONTH,
          type: 'month'
        };
      } else if (loopback % SECONDS_PER_DAY === 0) {
        valueToSave = {
          value: loopback / SECONDS_PER_DAY,
          type: 'day'
        };
      } else if (loopback % SECONDS_PER_HOUR === 0) {
        valueToSave = {
          value: loopback / SECONDS_PER_HOUR,
          type: 'hour'
        };
      } else if (loopback % SECONDS_PER_MINUTE === 0) {
        valueToSave = {
          value: loopback / SECONDS_PER_MINUTE,
          type: 'minute'
        };
      } else {
        valueToSave = {
          value: loopback,
          type: 'second'
        };
      }
    }

    return valueToSave;
  }, []);
  const [startValue, setStartValue] = useState(
    getLookBackValue(args.lookback?.start || 0)
  );
  const [endValue, setEndValue] = useState(
    getLookBackValue(args.lookback?.end || 0)
  );

  const formatAggregateFunctionList = (val) => {
    return (
      aggregateFunction[fields[val]?.type]?.map(({ value, label }) => ({
        value,
        label
      })) || [
        {
          value: 'collect_set',
          label: 'Collect Set'
        },
        {
          value: 'count',
          label: 'Count'
        },
        {
          value: 'count_distinct',
          label: 'Count Distinct'
        }
      ]
    );
  };

  useEffect(() => {
    // Aggregate Field
    const defaultOptions = formatFieldsForDropdown(fields);
    const aggregateFieldData = formatFieldsWithAPIData(
      selectedRules.function[functionIndex]?.args?.fieldList,
      defaultOptions,
      true
    );
    setAggregateField(aggregateFieldData);

    // Group By
    const groupByData = formatFieldsWithAPIData(
      selectedRules.function[functionIndex]?.args?.groupingKeysList,
      groupByOptions
    );
    setGroupByArr(groupByData);
    // FIN-13484: groupByOptions now follows applicableTo, so re-derive when it changes.
  }, [fields, groupByOptions]);

  useEffect(() => {
    // Aggregate function
    const defaultFunctionList = formatAggregateFunctionList(args.field);
    const aggregateFunctionData = formatFieldsWithAPIData(
      selectedRules.function[functionIndex]?.args?.aggregationFunctionList,
      defaultFunctionList
    );
    setAggregateFunctionList(orderBy(aggregateFunctionData, 'label', 'asc'));
  }, [args.field]);

  // ── FIN-12351: Perspective / Swap ─────────────────────────────────────────────────────────
  // Everything below is DERIVED from `args` on every render, never mirrored into local state.
  // `onChangeApplicableTo` replaces `args` wholesale, and a stale copy of the preset would then
  // show a control for a mapping that no longer exists (the mistake `densityArgs` makes).
  // `groupByArr` is this function's PICKER list: filtered to `triggeredOn` and narrowed by the BE
  // `groupingKeysList` allowlist. Right for the dropdown, too narrow for a resolver — see
  // resolverOptions in the parent. Falls back to groupByArr when the prop is absent.
  const spellingResolver = useMemo(
    () => buildSpellingResolver(resolverOptions || groupByArr),
    [resolverOptions, groupByArr]
  );
  // FIN-13506: the picker's own tag resolves through the full-catalogue resolver, so a stored key
  // outside the current option list renders "User ID" rather than the raw `user_id` column.
  const groupByTagRender = useMemo(
    () => renderFieldTagWith(spellingResolver.labelOf),
    [spellingResolver]
  );

  // FIN-13506: an empty Group By list is never "no data" — it is the BE allowlist
  // (`groupingKeysList`) intersected with the classes this function aggregates coming out empty,
  // which is an unusable rule, not an empty catalogue. Bare "No data" made that indistinguishable
  // from a failed load and left no way to tell WHICH keys were dropped. `undefined` keeps antd's
  // default whenever there is genuinely nothing to explain (a search that matches nothing).
  const groupByEmptyReason = useMemo(() => {
    // `groupingKeysList` is part of args (FraudAggregationFunctionArgs), so read it from the live
    // args rather than reaching back through selectedRules for the same value.
    const allowlist = args.groupingKeysList;
    const allowed = (
      Array.isArray(allowlist) ? allowlist : String(allowlist || '').split(',')
    ).filter(Boolean);
    if (groupByArr.length > 0 || allowed.length === 0) return undefined;
    const classes = normalizeApplicableTo(args.applicableTo, [triggeredOn]);
    return (
      <div className="groupby-empty-reason" data-testid="groupby-empty-reason">
        {`None of this indicator's permitted Group By keys (${allowed
          .map((k) => spellingResolver.labelOf(k))
          .join(', ')}) exist on the ${classes.join(
          ' and '
        )} events it aggregates, so there is
          nothing to select. The rule cannot match as configured — fix its Group By in the
          typology, or change Applicable To.`}
      </div>
    );
  }, [
    groupByArr,
    args.groupingKeysList,
    args.applicableTo,
    triggeredOn,
    spellingResolver
  ]);

  const storedGroupingKeys = useMemo(
    () => args.groupingKeys || [],
    [args.groupingKeys]
  );
  const mapping = args.groupingKeyToValueMapping || {};
  const groupingControl = useMemo(
    () =>
      controlFor(storedGroupingKeys, groupingKeyPairs, spellingResolver, {
        configPresent: pairConfigPresent
      }),
    [storedGroupingKeys, groupingKeyPairs, spellingResolver, pairConfigPresent]
  );
  const preset = presetFromMap(groupingControl, mapping, spellingResolver);

  // FIN-13534: a CROSS-TYPE pair cell carries no identity option, so `defaultOptionOf` is undefined
  // and nothing is preselected. Where the cell offers exactly ONE value there is no choice to make
  // — leaving it unset only yields a rule that never matches and a warning telling the analyst to
  // pick the single option in front of them. Select it for them.
  //
  // Deliberately narrow: only when there is no identity default (a same-type cell HAS one, and
  // silently redirecting there would change what the rule counts), only when nothing is already
  // selected, and only for a single option. Writes through the same handler the radio uses, so the
  // stored shape is identical to a manual click. Re-entry is impossible: once written,
  // `preset.selected` is defined and the guard below short-circuits.
  // Every condition below is a way the auto-selection could otherwise introduce an error the
  // analyst did not make, so each is required rather than merely likely:
  //   kind/cross-type  - a same-type cell HAS an identity default; auto-redirecting there would
  //                      silently change which direction the rule counts.
  //   no identity      - the same thing stated from the data side: never pick FOR the user when
  //                      "no redirection" is a legitimate, expressible choice.
  //   not custom       - a hand-authored or AFC-written map the drawer cannot explain is rendered
  //                      read-only and left untouched on save; overwriting it would lose it.
  //   mapping empty    - only fill a vacuum. Never replace an existing redirect.
  //   nothing selected - a preset already resolved means the user (or a prior save) chose.
  //   include-current  - a non-empty mapping with includeCurrentTransaction=true violates G2 and
  //                      BLOCKS the save. Writing the map here would hand the analyst a save error
  //                      they never caused. FIN-13532 disables that checkbox for cross-type, but a
  //                      rule stored before that fix can still carry the flag.
  //   single option    - with two or more there is a real choice; making it silently would be
  //                      guessing at the rule's meaning.
  const onlyPerspectiveOption =
    groupingControl.kind === 'perspective' &&
    isCrossTypeFunction &&
    defaultOptionOf(groupingControl, spellingResolver) === undefined &&
    preset.kind !== 'custom' &&
    Object.keys(mapping || {}).length === 0 &&
    preset.selected === undefined &&
    args.includeCurrentTransaction !== true &&
    (groupingControl.options || []).length === 1
      ? groupingControl.options[0]
      : undefined;

  // Fire AT MOST ONCE per (function, key, option). Writing into function args gives
  // `variantInfo.function` a new identity, which recomputes QueryBuilder's memoised `configValue`
  // and fires its `setConfig` effect — so an auto-selection that can re-trigger pumps a render
  // loop and React aborts with #185 ("Maximum update depth exceeded"). That is what changing
  // Applicable To hit: the rebuild in onChangeApplicableTo drops the mapping this effect had just
  // written, which made `onlyPerspectiveOption` defined again and re-armed the effect.
  //
  // The deps alone are not enough protection: they compare VALUES, so a mapping that is written
  // and then dropped returns the dep to its previous value and re-runs the effect. The ref records
  // that this exact shape was already handled, which no amount of state churn can undo.
  const autoSelectedRef = useRef(null);
  useEffect(() => {
    if (isReadOnly || onlyPerspectiveOption === undefined) return;
    const stamp =
      `${functionIndex}:${groupingControl.key}:${onlyPerspectiveOption}`;
    if (autoSelectedRef.current === stamp) return;
    autoSelectedRef.current = stamp;
    onChangeGroupingKeyMapping(
      mapFromPerspective(groupingControl.key, undefined, onlyPerspectiveOption),
      functionIndex
    );
  }, [onlyPerspectiveOption, isReadOnly]);
  // A mapping this drawer cannot explain (AFC-authored, hand-edited, or a pair seeded later) is
  // rendered read-only and left untouched on save — never normalised away.
  // FIN-13548: the per-key editor. Offered ONLY where no preset applies, so there is never more
  // than one editor for `groupingKeyToValueMapping` on screen and the two cannot disagree. The
  // radio keeps the single-key case and the Swap toggle the mutual pair; this covers the rest,
  // which previously had no control at all and therefore no way to clear a native-only warning.
  const perKeyEditable =
    !isReadOnly &&
    groupingControl.kind === 'none' &&
    (groupingControl.reason === 'multiple-non-mutual' ||
      groupingControl.reason === 'multiple-mutual-pairs');
  const perKeyRows = useMemo(
    () =>
      perKeyEditable
        ? perKeyControls(storedGroupingKeys, groupingKeyPairs, spellingResolver)
        : [],
    [perKeyEditable, storedGroupingKeys, groupingKeyPairs, spellingResolver]
  );
  const perKeyRowFor = (key) => perKeyRows.find((r) => r.storedKey === key);

  const isCustomMapping = preset.kind === 'custom';
  const strandedKeys = strandedGroupingKeys(
    storedGroupingKeys,
    mapping,
    groupingKeyPairs,
    spellingResolver
  );
  const readsAs = readsAsSentence({
    aggFunctionLabel:
      aggregateFunctionList.find((o) => o.value === args.aggregationFunction)
        ?.label || 'Aggregation',
    // The Aggregate Field is NOT a Group By option, so the grouping-key resolver cannot label it
    // — it would fall through to the raw column ("Sum of sender_amount_usd sent by ..."), which
    // is exactly the jargon this sentence exists to replace. Read it from the field catalogue,
    // which carries the legacy spelling as a hidden twin so a pre-FIN-12874 rule still resolves.
    fieldLabel:
      fields?.[args.field]?.label ||
      fields?.[toCanonicalId(args.field)]?.label ||
      spellingResolver.labelOf(args.field) ||
      'the selected field',
    groupingKeys: storedGroupingKeys,
    mapping,
    // Fallback is [triggeredOn], matching every other consumer in this file: an activity-triggered
    // variant whose function carries no applicableTo would otherwise be described as aggregating
    // "transactions ... sent by", which is the exact wrong-answer case the activity branch exists
    // to avoid. (normalizeApplicableTo returns early on Array.isArray, so [] never reaches it.)
    overActivity: normalizeApplicableTo(args.applicableTo, [
      triggeredOn
    ]).includes('activity'),
    eventNoun: triggeredOn,
    resolver: spellingResolver
  });
  // FIN-13860: offered against THIS function's Group By list (groupByArr, already narrowed by the
  // typology's groupingKeysList), so the hint never recommends a key the picker does not show.
  const readsAsHint = perspectiveHint({
    groupingKeys: storedGroupingKeys,
    mapping,
    overActivity: normalizeApplicableTo(args.applicableTo, [
      triggeredOn
    ]).includes('activity'),
    groupByOptions: groupByArr,
    resolver: spellingResolver
  });

  // Party words only stay meaningful while every option in the row belongs to a DIFFERENT party.
  // A row like [receiver.hashcode, sender.hashcode, sender.msisdn] would render "Receiver",
  // "Sender", "Sender" — two indistinguishable radios writing different mappings, which quietly
  // defeats the whole arity-N contract. Fall back to the field label whenever a party word would
  // collide within the same group.
  const perspectiveLabel = (fieldId, siblings = []) => {
    const party = partyOf(fieldId);
    if (!party) return spellingResolver.labelOf(fieldId);
    const collides =
      siblings.filter((o) => o !== fieldId && partyOf(o) === party).length > 0;
    if (collides) return spellingResolver.labelOf(fieldId);
    return party === 'sender' ? 'Sender' : 'Receiver';
  };

  const toSeconds = ({ type, value }) => (TIME_IN_SECONDS[type] || 0) * value;

  const checkDurationAndUpdate = () => {
    const startMs = toSeconds(startValue);
    const endMs = toSeconds(endValue);

    onChangeCalendarLookback(
      {
        start: startMs,
        end: endMs
      },
      functionIndex,
      'start'
    );
  };

  useEffect(() => {
    if (startValue.value || endValue.value) {
      checkDurationAndUpdate();
    }
  }, [startValue, endValue]);

  const onChangeDensityArgs = (val, key) => {
    let newArgs = { [key]: val };

    if (key === 'timestampPart') {
      newArgs['useBinRange'] = val === 'hour' ? 1 : 0;
      newArgs['minEventCount'] = 100;
    }

    setDensityArgs((prev) => {
      const updatedArgs = { ...prev, ...newArgs };
      onChangeSelectType(updatedArgs, functionIndex, 'aggregationFunctionArgs');
      return updatedArgs;
    });
  };

  return (
    <div className={style.aggregatetype}>
      <div className="aggragete--field-row">
        <div className="matchfield">
          <label style={{ display: 'flex' }}>
            Aggregate Field<span style={{ color: 'red' }}>*</span>
            {getToolTip(tooltips?.aggregate_field)}
          </label>
          <Select
            value={toCanonicalId(args.field)}
            disabled={isReadOnly}
            placeholder="Please Select"
            showSearch
            optionLabelProp="label"
            optionFilterProp="label"
            filterOption={fieldOptionFilter}
            onChange={(e) => onChangeSelectType(e, functionIndex, 'field')}
          >
            {groupFieldOptions(aggregateField).length
              ? groupFieldOptions(aggregateField).map(
                  ({ label: groupLabel, trail, items }) => (
                    <Select.OptGroup
                      key={`block:${groupLabel}`}
                      label={
                        <span className="field-block-path">{groupLabel}</span>
                      }
                    >
                      {items.map(({ value, label }) => (
                        <Select.Option key={value} value={value} label={label}>
                          {renderFieldOption(label, value, trail)}
                        </Select.Option>
                      ))}
                    </Select.OptGroup>
                  )
                )
              : aggregateField?.map(({ value, label }) => (
                  <Select.Option key={value} value={value} label={label}>
                    {renderFieldOption(label, value)}
                  </Select.Option>
                ))}
          </Select>
        </div>
        <div className="matchfield">
          <label style={{ display: 'flex' }}>
            Aggregate Function<span style={{ color: 'red' }}>*</span>
            {getToolTip(tooltips?.aggregate_function)}
          </label>
          <Select
            value={args.aggregationFunction}
            disabled={isReadOnly}
            placeholder="Please Select"
            options={aggregateFunctionList}
            onChange={(e) =>
              onChangeSelectType(e, functionIndex, 'aggregationFunction')
            }
          />
        </div>
        <div className="matchfield">
          <label style={{ display: 'flex' }}>
            Group By<span style={{ color: 'red' }}>*</span>
            {getToolTip(tooltips?.aggregate_group_by)}
          </label>
          <Select
            value={args.groupingKeys?.map(toCanonicalId)}
            disabled={isReadOnly}
            mode="multiple"
            placeholder="Please Select"
            optionLabelProp="label"
            optionFilterProp="label"
            filterOption={fieldOptionFilter}
            tagRender={groupByTagRender}
            notFoundContent={groupByEmptyReason}
            // Group By is the rightmost control in the row, and `index.scss` forces every select
            // dropdown to `width: 250px !important` — wider than this control. Left-aligned, that
            // overflows the drawer's right edge. rc-trigger cannot correct it: it positions the
            // popup at its natural width, and the !important rule widens it afterwards. Anchoring
            // the popup's right edge to the control instead makes it grow inwards.
            placement="bottomRight"
            onChange={(e) =>
              onChangeSelectType(e, functionIndex, 'groupingKeys')
            }
          >
            {groupFieldOptions(groupByArr).length
              ? groupFieldOptions(groupByArr).map(
                  ({ label: groupLabel, trail, items }) => (
                    <Select.OptGroup
                      key={`block:${groupLabel}`}
                      label={
                        <span className="field-block-path">{groupLabel}</span>
                      }
                    >
                      {items
                        .slice()
                        .sort((a, b) => a.label.localeCompare(b.label))
                        .map((module) => (
                          <Select.Option
                            key={module.value}
                            value={module.value}
                            label={module.label}
                          >
                            {renderFieldOption(
                              module.label,
                              module.value,
                              trail
                            )}
                          </Select.Option>
                        ))}
                    </Select.OptGroup>
                  )
                )
              : groupByArr
                  ?.slice()
                  ?.sort((a, b) => a.label.localeCompare(b.label))
                  ?.map((module) => {
                    return (
                      <Select.Option
                        key={module.value}
                        value={module.value}
                        label={module.label}
                      >
                        {renderFieldOption(module.label, module.value)}
                      </Select.Option>
                    );
                  })}
          </Select>
        </div>
      </div>
      {/* FIN-13484: the BE's advisory findings that name THIS indicator (validateWarnings emits
          "... on indicator '<name>'"). Rule-level ones without an indicator stay on the badge. */}
      {(() => {
        // FIN-13534 follow-up: these come from the last /validate run against the STORED shape, so
        // editing Group By left them unchanged until a save + reopen — four keys removed, four
        // warnings still on screen naming fields no longer selected. Drop the ones whose field is
        // gone so the panel shrinks with the edit. Warnings that name no grouping key (rule-level
        // findings) are untouched, and nothing NEW is invented here — the live per-key signal is
        // the `perspective-required-warning` alert below.
        // The BE names the READ field, which is the mapping VALUE when the key is redirected and
        // the key itself when it is not (validateWarnings: readField =
        // mapping.getOrElse(column, column)). So a warning is still live if its field is either a
        // selected Group By key OR a current mapping target — checking only the keys would drop a
        // genuine warning about a redirect the moment it was mapped.
        const sameKey = (a, b) =>
          spellingResolver.toCanonical(a) === spellingResolver.toCanonical(b);
        // FIN-13548: match the BE exactly — compare against the current READ FIELDS, which is
        // `mapping[key] || key` per key (validateWarnings: readField =
        // mapping.getOrElse(column, column)). The earlier test was "still a key OR still a mapping
        // target", which was too lenient in the one case that matters most: selecting a Perspective
        // redirects the read away from the key, so the warning is immediately stale, yet the key
        // itself is still selected and the warning stayed on screen until a save and reopen. It
        // still keeps the genuine case the old comment worried about — a warning naming the
        // redirect TARGET survives, because that target is now a read field.
        const currentReadFields = storedGroupingKeys.map(
          (k) => (mapping || {})[k] || k
        );
        const stillReadAt = (field) =>
          currentReadFields.some((f) => sameKey(f, field));
        const mine = (ruleWarnings || [])
          .filter((w) => w.includes(`'${item.name}'`))
          .filter((w) => {
            // FIN-13548: switching Triggered On makes every FOCAL finding for the old class stale
            // at once, and the field test below cannot see it — the field is unchanged, it is the
            // CLASS that moved. An activity-triggered rule grouped by `user_id` kept showing
            // "user_id is not part of Transaction" even though `user_id` is a native activity
            // column.
            //
            // Only TWO of the three messages name the focal class, and the distinction matters
            // (FraudAggregationFunction.validateWarnings):
            //   :384 "Grouping key read field F is not part of $focal"            → focal
            //   :375 "groupingKeyToValueMapping value V is not part of $focal"    → focal
            //   :369 "Grouping key K is not part of $t"  where t ∈ applicableTo   → NOT focal
            // Matching the third would drop the genuine cross-type finding, whose class is
            // deliberately the aggregated class and so differs from `triggeredOn` by design.
            const forClass =
              /(?:Grouping key read field \S+|groupingKeyToValueMapping value \S+) is not part of (Transaction|Activity) on indicator/.exec(
                w
              );
            if (
              forClass !== null &&
              forClass[1].toLowerCase() !== String(triggeredOn).toLowerCase()
            ) {
              return false;
            }
            const named = /Grouping key read field (\S+) is not part/.exec(w);
            return named === null || stillReadAt(named[1]);
          })
          // The BE tail "— the rule can never match; select a Perspective (mapping) for a
          // cross-type key" is advice for API consumers. On screen the Perspective control is
          // directly below with its own explanation, so the tail is repetition in the longest
          // message we show. Keep the finding, drop the instruction.
          .map((w) => w.replace(/\s*—\s*the rule can never match;.*$/, ''));
        // FIN-13455 (folded in): this BE advisory string names the READ field by its raw
        // technical path (e.g. "Grouping key read field
        // activity_event.beneficiary_context.beneficiary.beneficiary_party_id is not part of
        // ..."). Swap the raw field for its picker label via the resolver already in scope
        // for this component — same fix as the incomplete-rule footer message.
        // NOTE: applied at render (see the <li> below), so it composes with the tail-strip above.
        const displayWarning = (w) => {
          const named = /Grouping key read field (\S+) is not part/.exec(w);
          if (!named) return w;
          return w.replace(named[1], spellingResolver.labelOf(named[1]));
        };
        return mine.length > 0 ? (
          <Alert
            type="warning"
            showIcon
            data-testid="function-validation-warnings"
            style={{ marginBottom: 8 }}
            /* FIN-13548: "advisory, does not block save" was literally true but read as
               harmless. Since FIN-13538 the threshold-generation launch returns 422 on these, so
               the consequence is real — say it, because FIN-13538's whole complaint was that this
               warning was easy to miss. Save is still allowed on purpose: a rule the drawer cannot
               yet fix must not become unsaveable. */
            message="Validation warnings — save is allowed, but threshold generation will not launch until these are resolved"
            description={
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {mine.map((w) => (
                  <li key={w}>{displayWarning(w)}</li>
                ))}
              </ul>
            }
          />
        ) : null;
      })()}
      {/* FIN-12351 (PRD §4.4) — which party's activity this aggregation describes. The control is
          a preset over `groupingKeyToValueMapping`; the read-only row beneath it shows the map
          that actually gets saved, and the "Reads as" line states it in words. */}
      {storedGroupingKeys.length > 0 && (
        <div
          className={style.aggregatetype}
          data-testid="grouping-key-mapping-section"
        >
          {groupingControl.kind === 'perspective' && (
            <div>
              <label style={{ display: 'flex' }}>
                Perspective
                <Tooltip
                  title={
                    /* FIN-13431: the transaction wording (money sent / received, "the current
                       sender") only holds while pairs exist for transactions alone. Seeding
                       activity pairs makes this tooltip reachable on an activity variant, where
                       there is no counterparty and nothing is "sent". Keep the concrete
                       explanation where it is true and generalise otherwise. */
                    triggeredOn === 'transaction' ? (
                      <span>
                        Whose activity this aggregation describes. Group By
                        fixes
                        <strong> which direction</strong> is counted (grouping
                        on a sender field counts money sent, on a receiver field
                        money received); Perspective picks{' '}
                        <strong>whose</strong> value is looked up. Choosing the
                        non-default option is what makes &quot;received by the
                        current sender&quot; expressible.
                      </span>
                    ) : (
                      <span>
                        Whose activity this aggregation describes. Group By
                        fixes
                        <strong> which events</strong> are counted; Perspective
                        picks <strong>whose</strong> value is looked up — the
                        party on the current event, or a counterparty of it. It
                        never combines customers: a counterparty Perspective
                        counts that counterparty&apos;s own activity. The
                        &quot;Reads as&quot; line below states the result.
                      </span>
                    )
                  }
                >
                  <InfoCircleOutlined
                    twoToneColor="#000"
                    style={{ marginLeft: 6 }}
                  />
                </Tooltip>
              </label>
              <Radio.Group
                data-testid="perspective-radio-group"
                disabled={isReadOnly}
                value={preset.selected}
                onChange={(e) =>
                  onChangeGroupingKeyMapping(
                    mapFromPerspective(
                      groupingControl.key,
                      defaultOptionOf(groupingControl, spellingResolver),
                      e.target.value
                    ),
                    functionIndex
                  )
                }
              >
                {/* Render every option the pair row offers — arity is data, not two. */}
                {groupingControl.options.map((option) => (
                  <Radio key={option} value={option}>
                    {perspectiveLabel(option, groupingControl.options)}
                    {/* Identity is what "default" MEANS — deriving it from index 0 would put the
                        marker on the wrong option and selecting it would then write the redundant
                        entry this module never emits. A cross-type cell has no identity option, so
                        no radio is marked default and every choice writes the mapping
                        (FIN-13484: an unmapped activity key on a transaction rule is inert). */}
                    {option === defaultOptionOf(groupingControl, spellingResolver)
                      ? ' (default)'
                      : ''}
                  </Radio>
                ))}
              </Radio.Group>
              {/* FIN-13484: a cross-type cell has no identity option, so "nothing selected" is
                  not a default — it is a rule that never matches (the triggering event has no
                  value to read the key at). Say so where the choice is made. */}
              {defaultOptionOf(groupingControl, spellingResolver) === undefined &&
                preset.selected === undefined && (
                  <Alert
                    type="warning"
                    showIcon
                    data-testid="perspective-required-warning"
                    style={{ marginTop: 8 }}
                    message="Select a Perspective. This Group By key belongs to the aggregated events, not the triggering one, so without a Perspective the rule has nothing to look up and never matches."
                  />
                )}
            </div>
          )}
          {groupingControl.kind === 'swap' && (
            <div>
              <label style={{ display: 'flex' }}>
                {`Swap ${perspectiveLabel(groupingControl.canonicalA, [
                  groupingControl.canonicalA,
                  groupingControl.canonicalB
                ])} and ${perspectiveLabel(groupingControl.canonicalB, [
                  groupingControl.canonicalA,
                  groupingControl.canonicalB
                ])}`}
                <Tooltip
                  title={
                    triggeredOn === 'transaction'
                      ? 'Reads the same pair in the opposite direction — the aggregate of what flows from the current receiver back to the current sender.'
                      : 'Reads the same pair in the opposite direction — each key takes its value from the other.'
                  }
                >
                  <InfoCircleOutlined
                    twoToneColor="#000"
                    style={{ marginLeft: 6 }}
                  />
                </Tooltip>
              </label>
              <Switch
                disabled={isReadOnly}
                checked={preset.on === true}
                onChange={(checked) =>
                  onChangeGroupingKeyMapping(
                    mapFromSwap(
                      groupingControl.keyA,
                      groupingControl.keyB,
                      groupingControl.canonicalA,
                      groupingControl.canonicalB,
                      checked
                    ),
                    functionIndex
                  )
                }
              />
            </div>
          )}
          {groupingControl.kind === 'none' &&
            groupingControl.reason === 'not-configured-for-event' && (
              <div className="grouping-key-mapping__hint">
                {`No grouping-key pairs are configured for ${triggeredOn} events, so there is nothing to redirect here.`}
              </div>
            )}
          {groupingControl.kind === 'none' &&
            groupingControl.reason === 'no-pair-config' && (
              <div className="grouping-key-mapping__hint">
                Perspective is unavailable here — the sender/receiver pair
                configuration was not returned by the server. This is a
                deployment issue, not a property of the selected fields.
              </div>
            )}
          {groupingControl.kind === 'none' &&
            groupingControl.reason === 'unpaired' && (
              <div className="grouping-key-mapping__hint">
                {/* "sender/receiver" is transaction vocabulary. On an activity variant there is
                    no sender and no receiver, so naming them describes a pairing the analyst
                    could not have configured. Say "pair" and let the event noun carry the rest. */}
                {storedGroupingKeys.length > 1
                  ? `None of these fields has a configured pair for ${triggeredOn} events, so there is nothing to redirect.`
                  : `No configured pair for this field on ${triggeredOn} events, so there is nothing to redirect.`}
              </div>
            )}
          {groupingControl.kind === 'none' &&
            (groupingControl.reason === 'multiple-non-mutual' ||
              groupingControl.reason === 'multiple-mutual-pairs') && (
              <div className="grouping-key-mapping__hint">
                {/* FIN-13548: this used to end "so every key reads its own value", which was a
                    dead end — under native-only that rule cannot match and there was no control
                    to fix it. Each key is now editable in the table below. */}
                Perspective presets cover a single paired Group By field or one
                sender/receiver pair. This selection has more than that, so set
                each key&apos;s value individually below.
              </div>
            )}
          {isCustomMapping && (
            <div className="grouping-key-mapping__hint">
              This redirection was set outside the variant editor, so no preset
              above matches it. It is shown below and saved unchanged — pick an
              option above to replace it.
            </div>
          )}
          <table className="grouping-key-mapping">
            <thead>
              <tr>
                <th>Group by key</th>
                <th>
                  {/* The event noun follows the variant's Triggered On: an activity-triggered
                      variant has no transaction to take a value from. */}
                  {`Take value from (current ${triggeredOn})`}
                  {groupingControl.kind !== 'none' && !isCustomMapping
                    ? ' · set automatically'
                    : ''}
                </th>
              </tr>
            </thead>
            <tbody>
              {storedGroupingKeys.map((key) => {
                const row = perKeyRowFor(key);
                // Only a row with something OTHER than itself to offer earns a picker.
                const editable = row && row.reason === 'ok';
                return (
                  <tr key={key}>
                    <td>{spellingResolver.labelOf(key)}</td>
                    <td>
                      {editable ? (
                        <Select
                          data-testid={`grouping-key-value-${key}`}
                          style={{ minWidth: 220 }}
                          placeholder="Please Select"
                          value={mapping[key] || row.identity}
                          options={row.options.map((option) => ({
                            value: option,
                            label:
                              spellingResolver.labelOf(option) +
                              (option === row.identity ? ' (default)' : '')
                          }))}
                          onChange={(value) =>
                            onChangeGroupingKeyMapping(
                              mapWithKeyValue(mapping, key, row.identity, value),
                              functionIndex
                            )
                          }
                        />
                      ) : (
                        <>
                          {spellingResolver.labelOf(mapping[key] || key)}
                          {/* FIN-13548: say WHY this one is not editable rather than rendering a
                              silent identity the analyst cannot change. */}
                          {row && row.reason === 'no-pair-row' && (
                            <div className="grouping-key-mapping__hint">
                              No configured pair for this field, so it can only
                              read its own value.
                            </div>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="grouping-key-mapping__reads-as">
            <strong>Reads as:</strong> {readsAs}
          </div>
          {readsAsHint && (
            <div className="grouping-key-mapping__hint">{readsAsHint}</div>
          )}
          {strandedKeys.length > 0 && (
            <Alert
              type="warning"
              showIcon
              style={{ marginTop: 8 }}
              message={strandedKeysWarning(
                strandedKeys.map((k) => ({
                  label: spellingResolver.labelOf(k),
                  paired: isPairedKey(k, groupingKeyPairs, spellingResolver)
                })),
                triggeredOn
              )}
            />
          )}
        </div>
      )}
      {args.aggregationFunction === 'density' && (
        <div className="aggragete--field-row">
          <div className="matchfield">
            <label>
              Timespan Selection<span style={{ color: 'red' }}>*</span>
            </label>
            <Select
              value={densityArgs?.timestampPart}
              disabled={isReadOnly}
              placeholder="Please Select"
              options={TIMESPAN_OPTIONS}
              onChange={(val) => onChangeDensityArgs(val, 'timestampPart')}
            />
          </div>
          <div className="matchfield">
            <label>
              Bin Range<span style={{ color: 'red' }}>*</span>
            </label>
            <InputNumber
              disabled={isReadOnly}
              value={densityArgs?.useBinRange}
              min={densityArgs?.timestampPart === 'hour' ? 1 : 0}
              max={densityArgs?.timestampPart === 'hour' ? 24 : 1440}
              step={densityArgs?.timestampPart === 'hour' ? 1 : 5}
              parser={(value) => (value === '' ? '' : parseInt(value, 10))}
              formatter={(value) => (value ? value.toString() : '')}
              onChange={(val) => onChangeDensityArgs(val, 'useBinRange')}
            />
          </div>
          <div className="matchfield">
            <label>
              Min Transaction Count<span style={{ color: 'red' }}>*</span>
            </label>
            <InputNumber
              disabled={isReadOnly}
              value={densityArgs?.minEventCount}
              min={100}
              parser={(value) => (value === '' ? '' : parseInt(value, 10))}
              formatter={(value) => (value ? value.toString() : '')}
              onChange={(val) => onChangeDensityArgs(val, 'minEventCount')}
            />
          </div>
        </div>
      )}
      <div className={style.aggregatetype}>
        <div>
          <label style={{ display: 'flex' }}>
            Time Table<span style={{ color: 'red' }}>*</span>
            {getToolTip(tooltips?.aggregate_time_table)}
          </label>
          <CalendarTypeButton
            isReadOnly={isReadOnly}
            value={args.lookbackType}
            index={functionIndex}
            onChangeCalendarType={onChangeCalendarType}
          />
        </div>
        <label>
          Time Window<span style={{ color: 'red' }}>*</span>
        </label>
        <div className="aggragete--field-row" style={{ marginTop: '-15px' }}>
          <div className="matchfield">
            <label>From</label>
            <div className="multiple-inputs">
              <Input
                className="time-window-from"
                disabled={isReadOnly || args.lookbackType === 'global'}
                value={startValue.value}
                onChange={(e) => {
                  setStartValue((prev) => ({
                    ...prev,
                    value: e.target.value
                  }));
                  onDirtyField();
                }}
              />
              <Select
                id="start-select"
                value={startValue.type}
                placeholder="Please Select"
                disabled={isReadOnly || args.lookbackType === 'global'}
                onChange={(val) => {
                  setStartValue((prev) => ({
                    ...prev,
                    type: val
                  }));
                  onDirtyField();
                }}
                options={TIME_OPTIONS}
              />
            </div>
          </div>
          <div className="matchlist">
            <label>To</label>
            <div className="multiple-inputs">
              <Input
                disabled={isReadOnly || args.lookbackType === 'global'}
                value={endValue.value}
                onChange={(e) => {
                  setEndValue((prev) => ({
                    ...prev,
                    value: e.target.value
                  }));
                  onDirtyField();
                }}
              />
              <Select
                id="end-select"
                value={endValue.type}
                disabled={isReadOnly || args.lookbackType === 'global'}
                onChange={(val) => {
                  setEndValue((prev) => ({
                    ...prev,
                    type: val
                  }));
                  onDirtyField();
                }}
                options={TIME_OPTIONS}
                placeholder="Please Select"
              />
            </div>
          </div>
          <div
            className="matchlist"
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <Tooltip
              title={
                // FIN-14013: short, user-facing. The why (the current event
                // is not of the counted class) is in the comment above
                // includeCurrentLocked.
                isCrossTypeFunction
                  ? `"Include Current" does not apply: this function ` +
                    `counts ${counted} events.` +
                    (args.includeCurrentTransaction
                      ? ` Uncheck it to use a Perspective.`
                      : '')
                  : ''
              }
            >
              {/* A disabled antd control fires no mouse events, so the
                  Tooltip needs this wrapper to receive the hover — without
                  it the explanation is unreachable. */}
              <span style={{ display: 'inline-block' }}>
                <Checkbox
                  checked={args.includeCurrentTransaction}
                  disabled={includeCurrentLocked}
                  data-testid="include-current-checkbox"
                  onChange={(e) =>
                    onChangeSelectType(
                      e.target.checked,
                      functionIndex,
                      'includeCurrentTransaction'
                    )
                  }
                >
                  {`Include Current`}
                </Checkbox>
              </span>
            </Tooltip>
            <Tooltip title={tooltips?.aggregate_include_current}>
              <InfoCircleOutlined twoToneColor="#000" />
            </Tooltip>
          </div>
        </div>
        {isIncludeCurrentEndConflict(args) && (
          <Alert
            type="warning"
            showIcon
            message={INCLUDE_CURRENT_END_CONFLICT_HINT}
            style={{ marginTop: 8 }}
          />
        )}
        {/* FIN-12351 (G2): the current event sits on the axis that is NOT redirected, so counting
            it would add an outgoing event to an incoming total. The BE rejects this at rule load;
            surface it live rather than at save. */}
        {isMappingIncludeCurrentConflict(args) && (
          <Alert
            type="error"
            showIcon
            message={MAPPING_INCLUDE_CURRENT_HINT}
            style={{ marginTop: 8 }}
          />
        )}
      </div>
      <div className={style.aggregatetype}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <label style={{ margin: 0 }}>Aggregation Filter</label>
            <Tooltip
              title={
                <span>
                  Restricts which events are counted inside this function's
                  aggregation window. Applied in <strong>both</strong> Spark
                  batch precompute and Flink realtime state, so this filter
                  shapes the historical numbers your formula sees.
                </span>
              }
            >
              <InfoCircleOutlined
                style={{ color: '#8c8c8c', cursor: 'help' }}
              />
            </Tooltip>
          </div>
          {!isReadOnly && (
            <CopyFromGlobalLink
              variantInfo={variantInfo}
              typoIndex={typoIndex}
              variantIndex={variantIndex}
              functionIndex={functionIndex}
              currentTarget={args.aggregationCondition}
              setVariantRenderData={setVariantRenderData}
              onDirtyField={onDirtyField}
            />
          )}
        </div>
        <QueryBuilder
          typoIndex={typoIndex}
          variantInfo={variantInfo}
          variantIndex={variantIndex}
          functionIndex={functionIndex}
          type={`aggregationCondition`}
          itemNoun="function"
          isReadOnly={isReadOnly}
          allowNewIfEmpty={true}
          queryValue={args.aggregationCondition}
          setVariantRenderData={setVariantRenderData}
          onDirtyField={onDirtyField}
          applicableFields={fields}
          disableActions={disableActions}
        />
      </div>
    </div>
  );
};

const SegmentButton = ({ value, index, onChangeFunction, isReadOnly }) => {
  return (
    <div className={style.segmentButton}>
      <input
        disabled={isReadOnly}
        type="radio"
        name={`segment-btn-${index}`}
        checked={value.toLowerCase() === 'aggregation'}
        onChange={(e) => onChangeFunction(e, index, 'functionName')}
        value="aggregation"
        id={`aggregation-${index}`}
      />
      <label htmlFor={`aggregation-${index}`}>Aggregate</label>

      <input
        disabled={isReadOnly}
        type="radio"
        name={`segment-btn-${index}`}
        value="screening"
        checked={value.toLowerCase() === 'screening'}
        onChange={(e) => onChangeFunction(e, index, 'functionName')}
        id={`match-${index}`}
      />
      <label htmlFor={`match-${index}`}>Match</label>
    </div>
  );
};

const CalendarTypeButton = ({
  value = '',
  index,
  onChangeCalendarType,
  isReadOnly
}) => {
  return (
    <div className={style.segmentButton}>
      <input
        disabled={isReadOnly}
        type="radio"
        name={`cal-type-btn-${index}`}
        onChange={(e) => onChangeCalendarType(e, index, 'lookbackType')}
        checked={value.toLowerCase() === 'calendar'}
        value="calendar"
        id={`calendar-${index}`}
      />
      <label htmlFor={`calendar-${index}`}>Calendar</label>

      <input
        disabled={isReadOnly}
        type="radio"
        name={`cal-type-btn-${index}`}
        onChange={(e) => onChangeCalendarType(e, index, 'lookbackType')}
        value="continuous"
        checked={value.toLowerCase() === 'continuous'}
        id={`continuous-${index}`}
      />
      <label htmlFor={`continuous-${index}`}>Rolling</label>

      <input
        disabled={isReadOnly}
        type="radio"
        name={`cal-type-btn-${index}`}
        onChange={(e) => onChangeCalendarType(e, index, 'lookbackType')}
        value="global"
        checked={value.toLowerCase() === 'global'}
        id={`global-${index}`}
      />
      <label htmlFor={`global-${index}`}>Lifetime</label>
    </div>
  );
};

function mapStateToProps(state, ownProps = {}) {
  return {
    blackList: state.trmDashboard.blackList,
    metaData: state.amlsCommon.queryFields.data,
    // FIN-13484: per-typology / per-variant findings from the last /validate, so a function
    // card can show the warnings that name it.
    validationStatus:
      state.trmDashboard.scenarioValidationErrors?.status?.typologies || {}
  };
}

const Functions = FunctionsUnconnected;

export default connect(mapStateToProps, null)(Functions);
